import { Form, Input, InputNumber } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react';

import { VendorDevicePersistFields } from '../../../components/vendorDevice';
import { createComputeResourceRequiredRule } from '../domain/requiredRules';
import styles from '../index.module.less';
import type { ComputePaths } from '../types';

const FormItem = Form.Item;

interface ComputeResourcePersistFieldsProps {
  paths: ComputePaths;
  form: FormInstance;
  billModelId?: string;
  paymentTiming?: string;
  skuType?: string;
  resourceEditMode?: string;
  requireCpu?: boolean;
  requireMemory?: boolean;
  requireGpu?: boolean;
  cpuRequiredMessage?: string;
  memoryRequiredMessage?: string;
  gpuRequiredMessage?: string;
}

/** 主表单隐藏字段：规格类 + GPU 厂商设备常驻；计价时额外挂载 CPU/内存厂商设备，避免未出卡片时丢回填 */
export function ComputeResourcePersistFields({
  paths,
  form,
  billModelId,
  paymentTiming,
  skuType,
  resourceEditMode,
  requireCpu = false,
  requireMemory = false,
  requireGpu = false,
  cpuRequiredMessage,
  memoryRequiredMessage,
  gpuRequiredMessage,
}: ComputeResourcePersistFieldsProps) {
  const cpuRule =
    requireCpu && cpuRequiredMessage
      ? [createComputeResourceRequiredRule('cpu', form, paths, cpuRequiredMessage, billModelId, paymentTiming, skuType, resourceEditMode)]
      : undefined;
  const memoryRule =
    requireMemory && memoryRequiredMessage
      ? [
          createComputeResourceRequiredRule(
            'memory',
            form,
            paths,
            memoryRequiredMessage,
            billModelId,
            paymentTiming,
            skuType,
            resourceEditMode,
          ),
        ]
      : undefined;
  const gpuRule =
    requireGpu && gpuRequiredMessage
      ? [createComputeResourceRequiredRule('gpu', form, paths, gpuRequiredMessage, billModelId, paymentTiming, skuType, resourceEditMode)]
      : undefined;
  /** 容器 simple 模式只校验 cpu.request，避免 physicalCards 空字段重复打「CPU配置必填」 */
  const useCpuRequestRule = Boolean(paths.cpuRequest);

  return (
    <div className={styles.persistFields} aria-hidden>
      <FormItem field={paths.cpuCards} noStyle rules={useCpuRequestRule ? undefined : cpuRule}>
        <InputNumber />
      </FormItem>
      <FormItem field={paths.cpuCores} noStyle>
        <InputNumber />
      </FormItem>
      <FormItem field={paths.cpuLogical} noStyle>
        <InputNumber />
      </FormItem>
      {paths.cpuRequest ? (
        <FormItem field={paths.cpuRequest} noStyle rules={cpuRule}>
          <InputNumber />
        </FormItem>
      ) : null}
      <FormItem field={paths.memory} noStyle rules={memoryRule}>
        <InputNumber />
      </FormItem>
      <FormItem field={`${paths.gpu}.number`} noStyle rules={gpuRule}>
        <InputNumber />
      </FormItem>
      <FormItem field={`${paths.gpu}.memory`} noStyle>
        <Input />
      </FormItem>
      <FormItem field={`${paths.gpu}.name`} noStyle>
        <Input />
      </FormItem>
      <FormItem field={`${paths.gpu}.code`} noStyle>
        <Input />
      </FormItem>
      <FormItem field={`${paths.gpu}.card.name`} noStyle>
        <Input />
      </FormItem>
      <FormItem field={`${paths.gpu}.card.request`} noStyle>
        <InputNumber />
      </FormItem>
      <FormItem field={`${paths.gpu}.driverVersion`} noStyle>
        <Input />
      </FormItem>
      <FormItem field={`${paths.gpu}.cudaVersion`} noStyle>
        <Input />
      </FormItem>
      {/*
       * GPU 厂商设备与规格同源（resource.gpu），须在出卡片前常驻挂载；
       * 否则首次弹窗确认时 vendor/device 未注册，写回丢失，ItemBox 会显示「通用」。
       */}
      <VendorDevicePersistFields fieldPrefix={paths.gpu} />
      {billModelId ? (
        <>
          <VendorDevicePersistFields fieldPrefix={paths.cpuPricing} />
          <VendorDevicePersistFields fieldPrefix={paths.memoryPricing} />
        </>
      ) : null}
    </div>
  );
}
